import { NgTemplateOutlet } from '@angular/common';
import {
  signal,
  TemplateRef,
  TrackByFunction,
  ChangeDetectionStrategy,
  Component,
  Input,
  output,
  inject,
  input,
  computed,
} from '@angular/core';

import { trackByProp, trackByValue } from '@core/common-legacy';
import {
  HeadingComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
  DropdownComponent,
  IconButtonComponent,
} from '@core/user-interface-legacy';

import type { InsertPlaceholderInputDirective } from '../insert-placeholder-input.directive';
import { InsertPlaceholderDirective } from '../insert-placeholder.directive';
import type { Placeholder } from '../placeholder';
import { PlaceholderCategory, PlaceholderId } from '../placeholder';
import { PlaceholderSelectDropdownComponent } from '../placeholder-select-dropdown/placeholder-select-dropdown.component';
import { PlaceholderService } from '../placeholder.service';

@Component({
  selector: 'rt-placeholder-category-select-dropdown',
  templateUrl: './placeholder-category-select-dropdown.component.html',
  styleUrls: ['./placeholder-category-select-dropdown.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    HeadingComponent,
    InsertPlaceholderDirective,
    NgTemplateOutlet,
    PlaceholderSelectDropdownComponent,
    IconButtonComponent,
  ],
})
export class PlaceholderCategorySelectDropdownComponent {
  private readonly placeholderService = inject(PlaceholderService);

  public readonly active = input<Placeholder>(undefined!);
  public readonly activeTarget = input<InsertPlaceholderInputDirective>();
  public readonly placeholders = input<Placeholder[]>(undefined!);
  public readonly customNoCategoryPlaceholdersCollection = input<PlaceholderId[]>(undefined!);
  public readonly customCategories = input<PlaceholderCategory[]>(undefined!);
  public readonly header = input<string>(undefined!);
  @Input() public selectFooterTemplate: TemplateRef<any>;

  public select = output<Placeholder>();

  protected selectedSubcategory = signal<PlaceholderCategory | string>('');
  protected selectedCategory = signal<PlaceholderCategory | string>('');

  protected categories: PlaceholderCategory[];
  protected trackByCategoryName = trackByValue;
  protected trackByPlaceholderName: TrackByFunction<Placeholder> = trackByProp<Placeholder>({
    prop: 'id',
  });
  protected noCategoryPlaceholders: Placeholder[];
  protected noCategoryPlaceholdersCollection = [
    PlaceholderId.Company,
    PlaceholderId.Department,
    PlaceholderId.JobOffer,
    PlaceholderId.JobOfferAll,
  ];

  protected placeholderCategoryTranslationMap =
    this.placeholderService.placeholderCategoryTranslationMap;

  protected categoryPlaceholders = computed(() => {
    const selectedCategory = this.selectedCategory();
    return selectedCategory
      ? this.placeholders().filter(placeholder =>
          placeholder.id !== 'empty'
            ? this.placeholderService.categoryPlaceholdersMap[
                <PlaceholderCategory>selectedCategory
              ]?.includes(placeholder.id)
            : true,
        )
      : [];
  });
  protected subcategories = computed(() => {
    const selectedCategory = this.selectedCategory();
    return selectedCategory
      ? (
          this.placeholderService.subcategoryPlaceholdersMap[
            <PlaceholderCategory>selectedCategory
          ] || []
        ).filter(subcategory => {
          const subcategoryPlaceholders =
            this.placeholderService.categoryPlaceholdersMap[subcategory];
          return subcategoryPlaceholders.every(placeholder =>
            this.categoryPlaceholders()
              .map(p => p.id)
              .includes(placeholder),
          );
        })
      : [];
  });
  protected noSubcategoriesPlaceholders = computed(() => {
    const subcategoryPlaceholders = this.subcategories().flatMap(
      subcategory => this.placeholderService.categoryPlaceholdersMap[subcategory],
    );
    return this.categoryPlaceholders().filter(placeholder =>
      placeholder.id !== 'empty' ? !subcategoryPlaceholders.includes(placeholder.id) : true,
    );
  });

  public ngOnInit() {
    this.categories =
      this.customCategories() ||
      Object.values(PlaceholderCategory).filter(
        category =>
          this.placeholderService.isCategoryVisible(this.placeholders(), category) &&
          !this.placeholderService.isSubcategory(category),
      );

    this.noCategoryPlaceholders = (
      this.customNoCategoryPlaceholdersCollection() || this.noCategoryPlaceholdersCollection
    ).flatMap(placeholderId =>
      this.placeholderService.filterPlaceholders(this.placeholders(), placeholderId),
    );
  }

  protected setCategory(category: PlaceholderCategory | string = '', event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.selectedCategory.set(category);
  }

  protected setSubcategory(category: PlaceholderCategory | string = '', event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.selectedSubcategory.set(category);
  }

  protected withoutBrackets(str: string): string {
    return str.slice(1, -1);
  }
}
